import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { annotationService, Annotation, CreateAnnotationData } from '@/services/annotationService';
import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { useUndoRedoStore } from '@/stores/undoRedoStore';
import { createContext, useContext } from 'react';

// Context type for annotations - 'floor_plan' uses floor_plan_annotations table, 'elevation' uses elevation_items table
export type AnnotationContext = 'floor_plan' | 'elevation';
type AnnotationMutationOptions = {
  showSuccessToast?: boolean;
};

// React Context for annotation effectiveContext type
const AnnotationContextType = createContext<AnnotationContext>('floor_plan');
export const AnnotationContextProvider = AnnotationContextType.Provider;
export function useAnnotationContextType(): AnnotationContext {
  return useContext(AnnotationContextType);
}

// Measurement context - allows elevation annotations to provide their own measurement formatting
// When set, annotation components use this instead of useFloorPlanScale
interface MeasurementOverride {
  formatMeasurement: (pixels: number) => string;
  hasScale: boolean;
}
const MeasurementContext = createContext<MeasurementOverride | null>(null);
export const MeasurementProvider = MeasurementContext.Provider;
export function useMeasurementOverride(): MeasurementOverride | null {
  return useContext(MeasurementContext);
}

// Track recently mutated annotation IDs to prevent realtime handler from overwriting local changes
// This prevents flickering when dragging annotations - the realtime event arrives before React re-renders
const recentlyMutatedAnnotations = new Set<string>();
const MUTATION_PROTECTION_MS = 4000; // Protect fast drag handoffs from delayed realtime echoes.

/**
 * Mark an annotation as recently mutated to protect it from realtime overwrites
 */
export function markAnnotationAsMutated(annotationId: string): void {
  recentlyMutatedAnnotations.add(annotationId);
  setTimeout(() => {
    recentlyMutatedAnnotations.delete(annotationId);
  }, MUTATION_PROTECTION_MS);
}

/**
 * Check if an annotation was recently mutated locally
 * Used by realtime handlers to skip updates for in-flight mutations
 */
export function wasAnnotationRecentlyMutated(annotationId: string): boolean {
  return recentlyMutatedAnnotations.has(annotationId);
}

export function annotationQueryKey(entityId: string | undefined, context: AnnotationContext) {
  return ['annotations', entityId, context] as const;
}

export function useAnnotations(entityId: string | undefined, context?: AnnotationContext) {
  const contextFromProvider = useAnnotationContextType();
  const effectiveContext = context ?? contextFromProvider;
  const queryClient = useQueryClient();
  const addAction = useUndoRedoStore(state => state.addAction);
  const queryKey = annotationQueryKey(entityId, effectiveContext);

  // Query for fetching annotations
  const {
    data: annotations = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!entityId) return [];

      // For elevation effectiveContext, fetch from elevation_items table
      if (effectiveContext === 'elevation') {
        const supabase = await getAuthenticatedSupabaseClient();
        const { data, error } = await supabase
          .from('elevation_items')
          .select('*')
          .eq('elevation_id', entityId)
          .eq('item_type', 'annotation')
          .order('layer_order', { ascending: true });

        if (error) {
          logger.error('Error fetching elevation annotations:', error);
          throw error;
        }

        // Transform elevation_items to Annotation format
        // Note: elevation_items doesn't have a style column, style is stored in metadata.style
        return (data || []).map((item: any) => ({
          id: item.id,
          floor_plan_id: item.elevation_id, // Map to common field for compatibility
          type: item.metadata?.type || item.annotation_type || 'rectangle',
          data:
            item.annotation_data && Object.keys(item.annotation_data).length > 0
              ? item.annotation_data
              : item.metadata || {},
          style: item.metadata?.style || {},
          layer_order: item.layer_order || 0,
          created_at: item.created_at,
          updated_at: item.updated_at || item.created_at,
          created_by: item.created_by_clerk_id,
        })) as Annotation[];
      }

      // For floor_plan effectiveContext, use the standard annotation service
      return await annotationService.getAnnotations(entityId);
    },
    enabled: !!entityId,
    staleTime: 5000, // Consider data fresh for 5 seconds
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });

  // Helper function to create elevation annotation
  const createElevationAnnotation = async (
    data: CreateAnnotationData & { elevation_id?: string },
  ) => {
    const supabase = await getAuthenticatedSupabaseClient();

    // Get next layer order
    const { data: existingItems } = await supabase
      .from('elevation_items')
      .select('layer_order')
      .eq('elevation_id', data.elevation_id || data.floor_plan_id)
      .order('layer_order', { ascending: false })
      .limit(1);

    const nextLayerOrder = existingItems?.[0]?.layer_order ? existingItems[0].layer_order + 1 : 1;

    // Calculate position and dimensions from annotation data
    let x_position = 0;
    let y_position = 0;
    let width = 0;
    let height = 0;

    if (data.data.start && data.data.end) {
      // Line, arrow, measurement
      x_position = (data.data.start.x + data.data.end.x) / 2;
      y_position = (data.data.start.y + data.data.end.y) / 2;
      width = Math.abs(data.data.end.x - data.data.start.x);
      height = Math.abs(data.data.end.y - data.data.start.y);
    } else if (data.data.cx !== undefined && data.data.cy !== undefined) {
      // Circle
      x_position = data.data.cx;
      y_position = data.data.cy;
      width = (data.data.radius || 10) * 2;
      height = (data.data.radius || 10) * 2;
    } else {
      // Rectangle, text, image
      x_position = (data.data.x || 0) + (data.data.width || 0) / 2;
      y_position = (data.data.y || 0) + (data.data.height || 0) / 2;
      width = data.data.width || 100;
      height = data.data.height || 30;
    }

    // Note: elevation_items doesn't have a style column, store style inside metadata
    const { data: newItem, error } = await supabase
      .from('elevation_items')
      .insert({
        elevation_id: data.elevation_id || data.floor_plan_id,
        item_type: 'annotation',
        annotation_type: data.type,
        x_position,
        y_position,
        width,
        height,
        metadata: {
          type: data.type,
          style: data.style, // Store style inside metadata JSONB
          ...data.data,
        },
        annotation_data: data.data,
        layer_order: nextLayerOrder,
        is_visible: true,
        is_locked: false,
      })
      .select()
      .single();

    if (error) {
      throw error;
    }

    // Transform to Annotation format. Attach the raw inserted row under a
    // side-channel field (__rawItem) so the create.onSuccess can push it
    // directly into the elevation-items cache without a stale-replica refetch.
    // The field is deleted immediately after consumption so it doesn't leak
    // into application code.
    const annotation = {
      id: newItem.id,
      floor_plan_id: newItem.elevation_id,
      type: data.type,
      data: data.data,
      style: data.style || {},
      layer_order: nextLayerOrder,
      created_at: newItem.created_at,
      updated_at: newItem.created_at,
      created_by: newItem.created_by_clerk_id,
    } as Annotation;
    (annotation as any).__rawItem = newItem;
    return annotation;
  };

  // Helper function to update elevation annotation
  // Note: elevation_items doesn't have a style column, style is stored in metadata.style
  const updateElevationAnnotation = async (id: string, data: any) => {
    const supabase = await getAuthenticatedSupabaseClient();

    // We always need the existing metadata to preserve type/style across
    // partial updates. Previously we only fetched it when updating style,
    // and an update without data.type silently rewrote metadata.type to
    // 'rectangle' — which caused measurements (and any non-rectangle type)
    // to be rendered as rectangles after the next read, making them
    // disappear. Fetch unconditionally and merge.
    const { data: existingItem } = await supabase
      .from('elevation_items')
      .select('metadata, annotation_type')
      .eq('id', id)
      .single();
    const existingMetadata: any = existingItem?.metadata || {};
    const existingType: string =
      existingMetadata.type || existingItem?.annotation_type || 'rectangle';

    const updatePayload: any = {};

    if (data.data) {
      updatePayload.annotation_data = data.data;
      // Preserve existing type and style when updating data.
      updatePayload.metadata = {
        type: data.type || existingType,
        style: existingMetadata.style,
        ...data.data,
      };

      // Update position based on annotation type
      if (data.data.start && data.data.end) {
        updatePayload.x_position = (data.data.start.x + data.data.end.x) / 2;
        updatePayload.y_position = (data.data.start.y + data.data.end.y) / 2;
        updatePayload.width = Math.abs(data.data.end.x - data.data.start.x);
        updatePayload.height = Math.abs(data.data.end.y - data.data.start.y);
      } else if (data.data.cx !== undefined) {
        updatePayload.x_position = data.data.cx;
        updatePayload.y_position = data.data.cy;
        updatePayload.width = (data.data.radius || 10) * 2;
        updatePayload.height = (data.data.radius || 10) * 2;
      } else if (data.data.x !== undefined) {
        updatePayload.x_position = data.data.x + (data.data.width || 0) / 2;
        updatePayload.y_position = data.data.y + (data.data.height || 0) / 2;
        updatePayload.width = data.data.width;
        updatePayload.height = data.data.height;
      }
    }

    // Store style inside metadata, not as a separate column
    if (data.style) {
      updatePayload.metadata = {
        ...existingMetadata,
        ...(updatePayload.metadata || {}),
        style: data.style,
      };
    }

    if (data.layer_order !== undefined) {
      updatePayload.layer_order = data.layer_order;
    }

    const { data: updatedItem, error } = await supabase
      .from('elevation_items')
      .update(updatePayload)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      id: updatedItem.id,
      floor_plan_id: updatedItem.elevation_id,
      type: updatedItem.metadata?.type || updatedItem.annotation_type || 'rectangle',
      data: updatedItem.annotation_data || updatedItem.metadata || {},
      style: updatedItem.metadata?.style || {},
      layer_order: updatedItem.layer_order || 0,
      created_at: updatedItem.created_at,
      updated_at: updatedItem.updated_at || updatedItem.created_at,
      created_by: updatedItem.created_by_clerk_id,
    } as Annotation;
  };

  // Helper function to delete elevation annotation
  const deleteElevationAnnotation = async (id: string) => {
    const supabase = await getAuthenticatedSupabaseClient();
    const { error } = await supabase.from('elevation_items').delete().eq('id', id);
    if (error) {
      throw error;
    }
  };

  // Mutation for creating annotations with optimistic updates
  const createAnnotation = useMutation({
    mutationFn: async (data: CreateAnnotationData & { elevation_id?: string }) => {
      if (effectiveContext === 'elevation') {
        return createElevationAnnotation(data);
      }
      return annotationService.createAnnotation(data);
    },
    onMutate: async newAnnotationData => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousAnnotations = queryClient.getQueryData<Annotation[]>(queryKey);

      // Optimistically update to the new value
      const tempAnnotation: Annotation = {
        id: `temp-${Date.now()}`, // Temporary ID
        floor_plan_id:
          newAnnotationData.floor_plan_id || newAnnotationData.elevation_id || entityId || '',
        type: newAnnotationData.type,
        data: newAnnotationData.data,
        style: newAnnotationData.style || {},
        layer_order: newAnnotationData.layer_order || 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'current-user', // Temporary
      };

      queryClient.setQueryData<Annotation[]>(queryKey, (old = []) => [...old, tempAnnotation]);

      // Return a effectiveContext object with the snapshotted value
      return { previousAnnotations, tempAnnotation };
    },
    onSuccess: async (newAnnotation, variables, mutationContext) => {
      // Replace the temporary annotation with the real one
      queryClient.setQueryData<Annotation[]>(queryKey, (old = []) =>
        old.map(ann => (ann.id === mutationContext?.tempAnnotation.id ? newAnnotation : ann)),
      );

      // Elevation annotations are also read out of the elevation-items query
      // (used by the canvas for hit-testing, drag, rotation, etc). That cache
      // is independent of the annotations query.
      //
      // Earlier we tried refetchQueries here, but Supabase reads occasionally
      // hit a replica that hasn't yet seen the just-committed INSERT, so the
      // refetch returns the row set MINUS the new row. The cache then lags
      // by one mutation: each new create makes the PREVIOUS create draggable
      // but leaves the latest stuck. Push the inserted row directly into the
      // items cache so the canvas sees it immediately, no replica round-trip
      // required. The realtime subscription's INSERT handler is idempotent
      // (some-check before append), so it's harmless if it fires later too.
      if (effectiveContext === 'elevation' && entityId && (newAnnotation as any)?.__rawItem) {
        const rawItem = (newAnnotation as any).__rawItem;
        queryClient.setQueryData<any[]>(['elevation-items', entityId], (old = []) => {
          if (old.some(item => item.id === rawItem.id)) return old;
          return [...old, rawItem];
        });
        // Strip the side-channel field before downstream consumers see it.
        delete (newAnnotation as any).__rawItem;
      }

      // Add to undo history (only for floor plans)
      if (effectiveContext === 'floor_plan' && entityId) {
        addAction({
          type: 'CREATE_ANNOTATION',
          timestamp: Date.now(),
          floorPlanId: entityId,
          data: {
            annotation: newAnnotation,
          },
        });
      }
    },
    onError: (error, variables, mutationContext) => {
      logger.error('Failed to create annotation:', error);
      toast.error('Failed to create annotation');

      // If the mutation fails, use the effectiveContext returned from onMutate to roll back
      if (mutationContext?.previousAnnotations) {
        queryClient.setQueryData(queryKey, mutationContext.previousAnnotations);
      } else {
        // Force invalidate to resync with DB
        queryClient.invalidateQueries({ queryKey });
      }
    },
    onSettled: (data, error) => {
      // Invalidate queries on error to ensure cache is in sync
      if (error) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  // Mutation for updating annotations with optimistic updates
  const updateAnnotation = useMutation({
    mutationFn: async ({
      id,
      data,
      skipHistory,
    }: {
      id: string;
      data: any;
      skipHistory?: boolean;
    }) => {
      if (effectiveContext === 'elevation') {
        return updateElevationAnnotation(id, data);
      }
      return annotationService.updateAnnotation(id, data);
    },
    onMutate: async ({ id, data, skipHistory }) => {
      // Mark annotation as recently mutated to protect from realtime overwrites
      // This prevents flickering when the realtime event arrives before React re-renders
      markAnnotationAsMutated(id);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousAnnotations = queryClient.getQueryData<Annotation[]>(queryKey);

      // Get the old annotation data for undo history
      const oldAnnotation = previousAnnotations?.find(ann => ann.id === id);

      // Optimistically update to the new value
      queryClient.setQueryData<Annotation[]>(queryKey, (old = []) => {
        return old.map(annotation => {
          if (annotation.id === id) {
            const updated = { ...annotation, updated_at: new Date().toISOString() };
            // Handle nested data updates
            if (data.data) {
              updated.data = { ...annotation.data, ...data.data };
            }
            if (data.style) {
              updated.style = { ...annotation.style, ...data.style };
            }
            if (data.layer_order !== undefined) {
              updated.layer_order = data.layer_order;
            }
            return updated;
          }
          return annotation;
        });
      });

      // Return a effectiveContext object with the snapshotted value
      return { previousAnnotations, oldAnnotation };
    },
    onSuccess: (updatedAnnotation, variables, mutationContext) => {
      // For position-only updates (drag operations), trust the optimistic update
      // Don't overwrite with server response to prevent flicker when values
      // may differ slightly due to rounding or database transforms
      const isPositionOnlyUpdate =
        variables.data?.data && !variables.data?.style && variables.data?.layer_order === undefined;

      if (!isPositionOnlyUpdate) {
        // CRITICAL: Update cache with actual server data to ensure sync
        // This prevents text/style from disappearing if optimistic update differed
        queryClient.setQueryData<Annotation[]>(queryKey, (old = []) => {
          return old.map(annotation => {
            if (annotation.id === variables.id) {
              return updatedAnnotation; // Use server response, not optimistic data
            }
            return annotation;
          });
        });
      }

      // Keep the elevation-items query (read by ElevationCanvas for drag,
      // hit-testing, rotation) in sync with the annotations cache.
      if (effectiveContext === 'elevation' && entityId) {
        queryClient.invalidateQueries({ queryKey: ['elevation-items', entityId] });
      }

      // Add to undo history unless skipHistory is true (only for floor plans)
      if (
        effectiveContext === 'floor_plan' &&
        entityId &&
        mutationContext?.oldAnnotation &&
        !variables.skipHistory
      ) {
        addAction({
          type: 'UPDATE_ANNOTATION',
          timestamp: Date.now(),
          floorPlanId: entityId,
          data: {
            id: variables.id,
            oldData: mutationContext.oldAnnotation,
            newData: variables.data,
          },
        });
      }
    },
    onError: (error, variables, mutationContext) => {
      logger.error('Failed to update annotation:', error);
      toast.error('Failed to update annotation');

      // If the mutation fails, use the effectiveContext returned from onMutate to roll back
      if (mutationContext?.previousAnnotations) {
        queryClient.setQueryData(queryKey, mutationContext.previousAnnotations);
      }
    },
    onSettled: () => {
      // Don't show success toast for drag operations
      // The query will refetch after staleTime (5 seconds)
    },
  });

  // Mutation for deleting annotations
  const deleteAnnotation = useMutation({
    mutationFn: async ({ id }: { id: string; options?: AnnotationMutationOptions }) => {
      if (effectiveContext === 'elevation') {
        return deleteElevationAnnotation(id);
      }
      return annotationService.deleteAnnotation(id);
    },
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousAnnotations = queryClient.getQueryData<Annotation[]>(queryKey);

      // Get the annotation data for undo history
      const deletedAnnotation = previousAnnotations?.find(ann => ann.id === id);

      // Optimistically remove the annotation
      queryClient.setQueryData<Annotation[]>(queryKey, (old = []) =>
        old.filter(annotation => annotation.id !== id),
      );

      // Return a effectiveContext object with the snapshotted value
      return { previousAnnotations, deletedAnnotation };
    },
    onSuccess: (_, variables, mutationContext) => {
      if (variables.options?.showSuccessToast !== false) {
        toast.success('Annotation deleted');
      }

      // Invalidate and refetch to ensure cache is in sync with DB
      queryClient.invalidateQueries({ queryKey });

      // Keep the elevation-items query in sync (see create.onSuccess for why).
      if (effectiveContext === 'elevation' && entityId) {
        queryClient.invalidateQueries({ queryKey: ['elevation-items', entityId] });
      }

      // Add to undo history (only for floor plans)
      if (effectiveContext === 'floor_plan' && entityId && mutationContext?.deletedAnnotation) {
        addAction({
          type: 'DELETE_ANNOTATION',
          timestamp: Date.now(),
          floorPlanId: entityId,
          data: {
            annotation: mutationContext.deletedAnnotation,
          },
        });
      }
    },
    onError: (error, variables, mutationContext) => {
      logger.error('Failed to delete annotation:', error);
      toast.error('Failed to delete annotation');

      // If the mutation fails, use the effectiveContext returned from onMutate to roll back
      if (mutationContext?.previousAnnotations) {
        queryClient.setQueryData(queryKey, mutationContext.previousAnnotations);
      }
    },
  });

  return {
    annotations,
    isLoading,
    error,
    refetch,
    createAnnotation: createAnnotation.mutateAsync,
    updateAnnotation: updateAnnotation.mutateAsync,
    deleteAnnotation: (id: string, options?: AnnotationMutationOptions) =>
      deleteAnnotation.mutateAsync({ id, options }),
  };
}
